import React, { useMemo, useState } from "react";
import { Combine, X } from "lucide-react";
import { inr } from "./utils";

/** Bulk-merge modal — fires when the auditor selects 2+ rows (any ledger,
 *  same IT Block) and clicks "Merge into New Asset" on the bulk-action bar.
 *  Consolidates them into ONE new addition (e.g. 86 WIP construction
 *  vouchers -> one "Gokul Dry Yard" asset) for depreciation purposes. */
export function BulkMergeModal({ rows, onClose, onApply, busy }) {
  const latestAccDate = useMemo(
    () => rows.reduce((max, r) => ((r.accounting_date || "") > max ? r.accounting_date : max), ""),
    [rows],
  );
  const [description, setDescription] = useState("");
  const [ptuDate, setPtuDate] = useState((latestAccDate || "").slice(0, 10));

  const blockLabels = useMemo(() => new Set(rows.map(r => r.block_label || "")), [rows]);
  const sameBlock = blockLabels.size <= 1;
  const sumField = (f) => rows.reduce((s, r) => s + Number(r[f] || 0), 0);
  const totalInvoiceCost = sumField("invoice_cost");
  const ledgerNames = useMemo(
    () => Array.from(new Set(rows.map(r => r.ledger_name || "(unmapped)"))),
    [rows],
  );

  const canApply = sameBlock && description.trim() && ptuDate && rows.length >= 2;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
         data-testid="fa-bulk-merge-modal">
      <div className="bg-white border border-[#E5E5E0] w-full max-w-lg flex flex-col">
        <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-[#EDEDE7]">
          <div className="min-w-0">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-600">
              Merge into new asset
            </div>
            <div className="font-heading text-base mt-0.5">
              {rows.length} vouchers selected
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 p-1" data-testid="fa-bulk-merge-close">
            <X size={16}/>
          </button>
        </div>

        <div className="p-4 space-y-3">
          {!sameBlock && (
            <div className="text-[11.5px] text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1.5">
              Selected rows span {blockLabels.size} different IT Blocks — merging only works within a single block. Adjust your selection.
            </div>
          )}

          <div className="bg-[#FBFBF8] border border-[#E5E5E0] px-3 py-2 text-[11.5px] space-y-0.5">
            <div>Ledgers: <strong className="text-slate-800">{ledgerNames.join(", ")}</strong></div>
            <div>Total Invoice Cost: <strong className="font-mono text-slate-900">₹ {inr(totalInvoiceCost)}</strong></div>
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">
              Description of merged asset *
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Gokul Dry Yard"
              className="mt-1 w-full px-2 py-1.5 text-[13px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
              data-testid="fa-bulk-merge-description"
              autoFocus
            />
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">
              Put-to-use date *
            </label>
            <input
              type="date"
              value={ptuDate}
              onChange={(e) => setPtuDate(e.target.value)}
              className="mt-1 w-[160px] px-2 py-1.5 text-[13px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
              data-testid="fa-bulk-merge-ptu-date"
            />
            <div className="text-[10.5px] text-slate-500 mt-1">
              Decides the ≥180-day full/half depreciation rate for the entire merged amount — pre-filled with the latest voucher date; edit to the actual date the asset went into use.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#EDEDE7] bg-[#FBFBF8]">
          <button onClick={onClose} className="px-3 py-1.5 text-[12.5px] border border-slate-300 hover:bg-slate-100">Cancel</button>
          <button
            data-testid="fa-bulk-merge-apply"
            onClick={() => onApply({ description: description.trim(), put_to_use_date: ptuDate })}
            disabled={!canApply || busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            <Combine size={13}/> Merge {rows.length} into one asset
          </button>
        </div>
      </div>
    </div>
  );
}
